import { gsap } from 'gsap'
import debounce from 'lodash/debounce'

class FilterDomHandler {
	constructor(options) {
		const defaultOptions = {
			cbAfterFilter: false,
			displayStyle: 'block',
			// Advanced load-more options:
			enableLoadMore: false,
			nrInitialVisibleItems: 4,
			nrOfLoadItems: 4,
			nrOfLoadRounds: 0,
			manualAnimation: false,
			cbOnDomChange: null,
			// New sorting options:
			enableDomSorting: false, // Enable DOM reordering based on filter relevance
			sortByRelevance: true, // Sort by filter match relevance (most matches first)
		}
		this.options = Object.assign(defaultOptions, options)
		this.elContainer = this.options.elContainer || this.options.el

		// Initialize load-more state variables
		this.currentLoadRound = 0
		this.currentVisibleItems = this.options.nrInitialVisibleItems

		// Cache for original DOM order
		this.originalDomOrder = []

		this.filterItems = this.filterItems.bind(this)
		this.setupFilterableEls()
	}

	// Reset load-more state (used when filters change)
	resetLoadMore() {
		this.currentLoadRound = 0
		this.currentVisibleItems = this.options.nrInitialVisibleItems
	}

	refresh() {
		this.resetLoadMore()
		this.setupFilterableEls()
	}

	setupFilterableEls() {
		this.filterableEls = Array.from(this.options.el.querySelectorAll('[data-filters]'))

		this.filterItemsCache = this.filterableEls.map((item, index) => {
			let filters = item.getAttribute('data-filters') || ''
			return {
				el: item,
				id: item.getAttribute('data-id') || null,
				filters: filters.split(','),
				originalIndex: index, // Store original position
			}
		})

		// Cache original DOM order for restoration
		this.originalDomOrder = [...this.filterItemsCache]

		// Initially apply the filter (without animation)
		this.filterItems(true)
	}

	// Calculate relevance score for sorting
	calculateRelevanceScore(item, filterState = null) {
		const currentFilterState = filterState || this.filterState

		if (!currentFilterState || !item) return 0

		let score = 0
		let totalActiveFilters = 0

		currentFilterState.forEach((group) => {
			if (group.active.length > 0) {
				totalActiveFilters += group.active.length

				// Count matches in this group
				const groupMatches = group.active.filter((filter) => item.filters.indexOf(filter) > -1).length

				score += groupMatches
			}
		})

		// Return percentage of matched filters
		return totalActiveFilters > 0 ? score / totalActiveFilters : 0
	}

	// Sort items by relevance or restore original order
	getSortedItems(items) {
		if (!this.options.enableDomSorting) {
			return items
		}

		const hasActiveFilters = this.filterState ? this.filterState.some((group) => group.active.length > 0) : false

		if (!hasActiveFilters) {
			// No active filters: restore original order
			return [...items].sort((a, b) => a.originalIndex - b.originalIndex)
		}

		if (this.options.sortByRelevance) {
			// Sort by relevance score (highest first)
			return [...items].sort((a, b) => {
				const scoreA = this.calculateRelevanceScore(a)
				const scoreB = this.calculateRelevanceScore(b)

				// If scores are equal, maintain original order
				if (scoreA === scoreB) {
					return a.originalIndex - b.originalIndex
				}

				return scoreB - scoreA // Descending order
			})
		}

		return items
	}

	// Reorder DOM elements to match sorted order
	reorderDomElements(sortedMatchedItems) {
		if (!this.options.enableDomSorting) return

		const container = this.options.el

		// Create complete sorted order: matched items first, then unmatched in original order
		const unmatchedItems = this.filterItemsCache.filter((item) => !sortedMatchedItems.includes(item)).sort((a, b) => a.originalIndex - b.originalIndex)

		const completeOrderedList = [...sortedMatchedItems, ...unmatchedItems]

		// Get reference point for insertion
		const firstFilterableEl = this.filterableEls[0]

		if (!firstFilterableEl || firstFilterableEl.parentNode !== container) return

		// Remove all filterable elements from DOM
		this.filterableEls.forEach((el) => {
			if (el.parentNode === container) {
				el.remove()
			}
		})

		// Insert elements in new order at the original position (no animation)
		const fragment = document.createDocumentFragment()
		completeOrderedList.forEach((item) => {
			fragment.appendChild(item.el)
		})

		container.appendChild(fragment)
	}

	applyFilterState(filterState, noAnimation = false) {
		this.filterState = filterState
		// Reset load-more state when filter changes
		this.resetLoadMore()
		this.filterItems(noAnimation)
	}

	filterItems(noAnimation, isLoadMore = false) {
		const hasActiveFilters = this.filterState ? this.filterState.some((group) => group.active.length > 0) : false

		const allMatchedItems = this.getMatchedItems()

		// Sort items based on relevance or original order
		const sortedMatchedItems = this.getSortedItems(allMatchedItems)

		// Determine which items to show based on the "enableLoadMore" setting
		let itemsToShow
		if (this.options.enableLoadMore) {
			// Load-more functionality enabled: limit visible items
			itemsToShow = sortedMatchedItems.slice(0, this.currentVisibleItems)
		} else {
			// Load-more functionality disabled: show all matched items
			itemsToShow = sortedMatchedItems
		}

		// For load more, determine which items are newly added
		let newItems = []
		if (isLoadMore) {
			newItems = this._alreadyVisibleItems ? itemsToShow.filter((item) => !this._alreadyVisibleItems.includes(item)) : itemsToShow
		}

		// Hide items that are not in itemsToShow
		const itemsToHide = this.filterItemsCache.filter((item) => itemsToShow.indexOf(item) === -1)

		// Make sure we are not unnecessarily hiding items that are already hidden
		const elsToHide = itemsToHide.map((item) => item.el)
		const elsToShow = itemsToShow.map((item) => item.el)

		const visibleElsToHide = elsToHide.filter((el) => el.offsetParent !== null)
		const hiddenElsToShow = elsToShow.filter((el) => el.offsetParent === null)

		if (visibleElsToHide.length <= 0 && hiddenElsToShow.length <= 0) {
			return
		}

		const domChangeArgs = {
			nrOfResults: itemsToShow.length,
			nrOfVisibleItems: this.currentVisibleItems,
			nrOfMatchedItems: allMatchedItems.length,
			filterState: this.filterState,
			elsToHide,
			elsToShow,
			sortedItems: sortedMatchedItems, // Include sorted items info
		}

		// Handle animations
		if (!this.options.manualAnimation) {
			if (noAnimation) {
				if (elsToHide.length) gsap.set(elsToHide, { display: 'none' })

				// Reorder DOM elements after hiding (no animation, only for non-loadMore)
				if (!isLoadMore) {
					this.reorderDomElements(sortedMatchedItems)
				}

				if (elsToShow.length) gsap.set(elsToShow, { display: this.options.displayStyle })
				if (this.options.cbAfterFilter) this.options.cbAfterFilter()
			} else {
				if (isLoadMore && newItems.length) {
					// Only animate newly visible items
					const newEls = newItems.map((item) => item.el)
					if (newEls.length) {
						gsap.set(newEls, {
							opacity: 0,
							display: this.options.displayStyle,
						})
						gsap.to(newEls, { opacity: 1, duration: 0.5, stagger: 0.05 })
					}
				} else {
					// Full filter animation: fade out container then fade in items
					gsap.to(this.elContainer, { opacity: 0, duration: 0.2 }).then(() => {
						if (hasActiveFilters) {
							this.elContainer.classList.add('is-filtered')
						} else {
							this.elContainer.classList.remove('is-filtered')
						}

						if (elsToHide.length) gsap.set(elsToHide, { display: 'none' })

						// Reorder DOM elements after hiding but before showing (no animation)
						if (!isLoadMore) {
							this.reorderDomElements(sortedMatchedItems)
						}

						if (elsToShow.length)
							gsap.set(elsToShow, {
								opacity: 0,
								display: this.options.displayStyle,
							})
						if (this.options.cbAfterFilter) this.options.cbAfterFilter()
						gsap.set(this.elContainer, { opacity: 1 })

						if (this.options.cbOnDomChange) {
							this.options.cbOnDomChange(domChangeArgs)
						}

						if (elsToShow.length) {
							gsap.to(elsToShow, {
								opacity: 1,
								duration: 0.5,
								stagger: 0.05,
							})
						}
					})
				}
			}
		} else {
			// Reorder DOM elements for manual animation mode too
			if (!isLoadMore) {
				this.reorderDomElements(sortedMatchedItems)
			}

			if (this.options.cbOnDomChange) {
				this.options.cbOnDomChange(domChangeArgs)
			}
		}

		// Save the currently visible items for future load-more calls
		this._alreadyVisibleItems = itemsToShow
	}

	// ...existing code...
	filterMatch(item, filterState = null) {
		// Use passed filterState or fall back to this.filterState
		const currentFilterState = filterState || this.filterState

		if (!currentFilterState) return true
		if (!item) return true

		let activeGroups = this.filterState.filter((group) => group.active.length > 0)
		if (!activeGroups.length) return true
		let hasMatch = true

		activeGroups.forEach((group) => {
			// Check if any active filter in this group is matched
			const hasGroupMatch = group.active.some((filter) => item.filters.indexOf(filter) > -1)
			if (!hasGroupMatch) hasMatch = false
		})

		return hasMatch
	}

	getUnMatchedItems() {
		return this.filterItemsCache.filter((item) => !this.filterMatch(item))
	}

	getMatchedItems() {
		return this.filterItemsCache.filter((item) => this.filterMatch(item))
	}

	// Called on "Load More" button click
	onLoadMore() {}
	loadMore() {
		this.currentLoadRound++

		const allMatchedItems = this.getMatchedItems()

		// Check if there is a limit to load rounds.
		// If the number of rounds reached the limit, reveal all remaining items.
		if (this.options.nrOfLoadRounds > 0 && this.currentLoadRound >= this.options.nrOfLoadRounds) {
			this.currentVisibleItems = allMatchedItems.length
		} else {
			// Otherwise, add a batch of items.
			this.currentVisibleItems = Math.min(allMatchedItems.length, this.currentVisibleItems + this.options.nrOfLoadItems)
		}
		this.filterItems(false, true)
	}

	destroy() {
		if (this.elBtnLoadMore) {
			this.elBtnLoadMore.removeEventListener('click', this.bndOnLoadMore)
		}
	}
}

export default FilterDomHandler
